import { useReducedMotion } from 'motion/react';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import type { SopFlowHealthIssue, SopFlowHealthIssueSegment } from '@/lib/sops-workflow-author';

type StepSegment = Extract<SopFlowHealthIssueSegment, { kind: 'step' }>;

function StepBadge({
  segment,
  variant = 'secondary',
  pulse = false,
}: {
  segment: StepSegment;
  variant?: 'secondary' | 'outline' | 'destructive';
  pulse?: boolean;
}) {
  const resolvedVariant = segment.loopReentry ? 'destructive' : variant;
  return (
    <Badge
      variant={resolvedVariant}
      className={cn(
        'shrink-0 px-1.5 py-0 font-mono text-[11px] font-semibold leading-none',
        segment.loopReentry && pulse && 'animate-[sop-flow-loop-pulse_2s_ease-in-out_infinite]',
      )}
    >
      Step {segment.displayNumber}
    </Badge>
  );
}

function SopFlowHealthIssueSegments({
  segments,
  stepBadgeVariant = 'secondary',
}: {
  segments: SopFlowHealthIssueSegment[];
  stepBadgeVariant?: 'secondary' | 'outline' | 'destructive';
}) {
  return (
    <>
      {segments.map((segment, index) => {
        if (segment.kind === 'text') {
          return <span key={`text-${index}`}>{segment.text}</span>;
        }
        return (
          <span key={`step-${segment.stepId}-${index}`} className="inline-flex items-baseline gap-1">
            <StepBadge segment={segment} variant={stepBadgeVariant} />
            {segment.title ? (
              <span className="font-medium text-foreground/90">&ldquo;{segment.title}&rdquo;</span>
            ) : null}
          </span>
        );
      })}
    </>
  );
}

type StackedRow =
  | { kind: 'step'; segment: StepSegment; suffix?: string }
  | { kind: 'text'; text: string }
  | { kind: 'loop-label' }
  | { kind: 'arrow-down' };

function parseInterStepText(text: string): {
  branch?: string;
  connector?: 'links-to' | 'loop';
  other?: string;
} {
  const branchMatch = text.match(/^\s*(\((?:Yes|No)\))\s*/);
  const branch = branchMatch?.[1];
  let rest = (branchMatch ? text.slice(branchMatch[0].length) : text).trim();
  if (!rest) return { branch };
  if (rest === '→' || rest === '↓') return { branch, other: rest };
  if (rest === 'links to' || rest === 'links to.') return { branch, connector: 'links-to' };
  if (rest === 'Loop:' || rest === '. Loop:') return { branch, connector: 'loop' };
  if (rest === '.') return branch ? { branch: `${branch}.` } : { other: '.' };
  return { branch, other: rest };
}

function buildStackedRows(segments: SopFlowHealthIssueSegment[]): StackedRow[] {
  const rows: StackedRow[] = [];

  for (const segment of segments) {
    if (segment.kind === 'step') {
      rows.push({ kind: 'step', segment });
      continue;
    }

    const parsed = parseInterStepText(segment.text);
    if (parsed.branch) {
      const last = rows[rows.length - 1];
      if (last?.kind === 'step') {
        last.suffix = parsed.branch;
      }
    }
    if (parsed.connector === 'links-to') {
      rows.push({ kind: 'text', text: 'links to' });
    } else if (parsed.connector === 'loop') {
      rows.push({ kind: 'loop-label' });
    } else if (parsed.other) {
      if (parsed.other === '.') {
        const last = rows[rows.length - 1];
        if (last?.kind === 'step') {
          last.suffix = last.suffix ? `${last.suffix}.` : '.';
        } else {
          rows.push({ kind: 'text', text: parsed.other });
        }
      } else if (parsed.other === '→' || parsed.other === '↓') {
        rows.push({ kind: 'arrow-down' });
      } else {
        rows.push({ kind: 'text', text: parsed.other });
      }
    }
  }

  return rows;
}

function stackedRowKey(row: StackedRow, index: number): string {
  switch (row.kind) {
    case 'loop-label':
      return `loop-${index}`;
    case 'arrow-down':
      return `arrow-${index}`;
    case 'text':
      return `text-${index}`;
    case 'step':
      return `step-${row.segment.stepId}-${index}`;
  }
}

function renderStackedRowCells(
  row: StackedRow,
  options: {
    stepBadgeVariant: 'secondary' | 'outline' | 'destructive';
    pulseLoopSteps: boolean;
    loopLabelClassName?: string;
  },
) {
  if (row.kind === 'loop-label') {
    return (
      <>
        <span
          className={cn(
            'text-[11px] font-semibold uppercase tracking-wide text-destructive/80',
            options.loopLabelClassName,
          )}
        >
          Loop
        </span>
        <span aria-hidden />
      </>
    );
  }
  if (row.kind === 'arrow-down') {
    return (
      <>
        <span
          className="flex justify-center self-center py-0.5 text-xs text-muted-foreground/70"
          aria-hidden
        >
          ↓
        </span>
        <span aria-hidden />
      </>
    );
  }
  if (row.kind === 'text') {
    return <span className="col-span-2 text-muted-foreground/90">{row.text}</span>;
  }
  return (
    <>
      <StepBadge
        segment={row.segment}
        variant={options.stepBadgeVariant}
        pulse={options.pulseLoopSteps}
      />
      <span className="flex min-w-0 flex-wrap items-baseline gap-x-1 gap-y-0.5">
        {row.segment.title ? (
          <span className="min-w-0 font-medium text-foreground/90">&ldquo;{row.segment.title}&rdquo;</span>
        ) : null}
        {row.suffix ? <span className="text-muted-foreground/90">{row.suffix}</span> : null}
      </span>
    </>
  );
}

function renderStackedRows(
  rows: StackedRow[],
  options: {
    stepBadgeVariant: 'secondary' | 'outline' | 'destructive';
    pulseLoopSteps: boolean;
    loopLabelClassName?: string;
  },
) {
  return (
    <div className="grid grid-cols-[auto_1fr] gap-x-1.5 gap-y-0.5">
      {rows.map((row, index) => (
        <div
          key={stackedRowKey(row, index)}
          className="col-span-2 grid grid-cols-subgrid items-baseline gap-x-1.5"
        >
          {renderStackedRowCells(row, options)}
        </div>
      ))}
    </div>
  );
}

function SopFlowHealthIssueSegmentsStacked({
  segments,
  stepBadgeVariant = 'secondary',
}: {
  segments: SopFlowHealthIssueSegment[];
  stepBadgeVariant?: 'secondary' | 'outline' | 'destructive';
}) {
  const reduceMotion = useReducedMotion() ?? false;
  const pulseLoopSteps = !reduceMotion;
  const rows = buildStackedRows(segments);
  const stepCount = rows.filter((row) => row.kind === 'step').length;
  if (stepCount < 2) {
    return (
      <p>
        <SopFlowHealthIssueSegments segments={segments} stepBadgeVariant={stepBadgeVariant} />
      </p>
    );
  }

  const loopLabelIndex = rows.findIndex((row) => row.kind === 'loop-label');
  if (loopLabelIndex >= 0) {
    const connectionRows = rows.slice(0, loopLabelIndex);
    const loopRows = rows.slice(loopLabelIndex);
    const rowOptions = { stepBadgeVariant, pulseLoopSteps };

    return (
      <div className="space-y-2">
        {connectionRows.length ? (
          <div className="rounded-md border border-border/60 bg-muted/25 px-2.5 py-2">
            {renderStackedRows(connectionRows, rowOptions)}
          </div>
        ) : null}
        <div className="rounded-md border border-destructive/25 bg-destructive/5 px-2.5 py-2">
          {renderStackedRows(loopRows, rowOptions)}
        </div>
      </div>
    );
  }

  return renderStackedRows(rows, { stepBadgeVariant, pulseLoopSteps });
}

export function SopFlowHealthIssueLabel({ issue }: { issue: SopFlowHealthIssue }) {
  if (issue.labelSegments?.length) {
    return (
      <span className="inline-flex flex-wrap items-baseline gap-x-0.5 font-medium">
        <SopFlowHealthIssueSegments segments={issue.labelSegments} />
      </span>
    );
  }
  return <span className="font-medium">{issue.label}</span>;
}

export function SopFlowHealthIssueMessage({ issue }: { issue: SopFlowHealthIssue }) {
  if (issue.body?.length) {
    return (
      <div className="mt-1 space-y-1.5 text-xs leading-relaxed text-muted-foreground">
        <SopFlowHealthIssueSegmentsStacked segments={issue.body} />
        {issue.hint?.length ? (
          <div className="mt-2 rounded-md border border-warning/35 bg-warning/10 px-2.5 py-2 text-xs leading-relaxed dark:border-warning/30">
            <span className="font-semibold text-foreground">Fix: </span>
            <SopFlowHealthIssueSegments segments={issue.hint} stepBadgeVariant="outline" />
          </div>
        ) : null}
        <style>{`
          @keyframes sop-flow-loop-pulse {
            0%, 100% { box-shadow: 0 0 0 0 transparent; }
            50% { box-shadow: 0 0 0 3px hsl(var(--destructive) / 0.35); }
          }
        `}</style>
      </div>
    );
  }
  return <span className="mt-0.5 block text-xs leading-relaxed text-muted-foreground">{issue.message}</span>;
}
